// Adapted from herdr voice.ts, Copyright (c) 2026 devswha (MIT).
import { VoiceEvent } from '@pen/shared';
import type { z } from 'zod';

export type VoiceText = { text: string; phase: 'raw' | 'polished'; take: number };
export const HOLD_MS = 300;
export const SPEECH_PREROLL_MS = 350;
export const SPEECH_HANG_MS = 700;
export const MAX_AUDIO_BYTES = 8 * 1024 * 1024;

export function pickRecorderMime(supports: (type: string) => boolean): string | null {
  return ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/wav'].find(supports) ?? null;
}
export function classifyRelease(ms: number): 'finish' | 'keep' { return ms >= HOLD_MS ? 'finish' : 'keep'; }
export function smoothLevel(previous: number, target: number, dt: number): number {
  return previous + (target - previous) * (1 - Math.exp(-Math.max(0, dt) / (target > previous ? 60 : 250)));
}
export function levelFromRms(rms: number): number {
  return rms > 0 ? Math.min(1, Math.max(0, (20 * Math.log10(rms) + 60) / 50)) : 0;
}

/** Learns from quiet readings only, never raising the floor during a spoken sentence. */
export function createSpeechGate() {
  let open = false;
  let everOpened = false;
  let lastLoudAt = -Infinity;
  let floor = 0.003;
  return {
    get open() { return open; },
    get everOpened() { return everOpened; },
    get lastLoudAt() { return lastLoudAt; },
    step(rms: number, now: number): 'open' | 'close' | null {
      const loud = rms >= Math.max(0.006, floor * 3);
      if (loud) lastLoudAt = now;
      else floor = rms < floor ? rms : floor + (rms - floor) * 0.01;
      if (loud && !open) { open = true; everOpened = true; return 'open'; }
      if (!loud && open && now - lastLoudAt >= SPEECH_HANG_MS) { open = false; return 'close'; }
      return null;
    },
  };
}

/** Validates the shared binding, including a trailing line and split UTF-8 characters. */
export async function* readVoiceEvents(body: ReadableStream<Uint8Array>): AsyncGenerator<z.infer<typeof VoiceEvent>> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finished = false;
  try {
    while (!finished) {
      const { done, value } = await reader.read();
      finished = done;
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = done ? '' : lines.pop() ?? '';
      for (const line of lines) if (line.trim()) yield VoiceEvent.parse(JSON.parse(line));
    }
  } finally {
    if (!finished) await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

export function insertAtCaret(value: string, selectionStart: number, selectionEnd: number, text: string) {
  const from = Math.max(0, Math.min(selectionStart, selectionEnd, value.length));
  const to = Math.min(value.length, Math.max(selectionStart, selectionEnd, from));
  const spoken = text.trim();
  if (!spoken) return { value, start: from, end: to };
  const before = value.slice(0, from);
  const after = value.slice(to);
  const lead = before && !/\s$/.test(before) ? ' ' : '';
  const trail = after && !/^\s/.test(after) ? ' ' : '';
  const start = before.length + lead.length;
  return { value: before + lead + spoken + trail + after, start, end: start + spoken.length };
}
export function replaceIfUnchanged(value: string, range: { start: number; end: number }, expected: string, replacement: string) {
  if (range.start < 0 || range.end > value.length || value.slice(range.start, range.end) !== expected) return null;
  return { value: value.slice(0, range.start) + replacement + value.slice(range.end), end: range.start + replacement.length };
}
export interface InsertedSpan { start: number; end: number; text: string }
export type DictationSpans = Map<number, InsertedSpan> & { box?: string; latestTake?: number };
function shiftSpans(spans: DictationSpans, from: number, to: number, delta: number): void {
  for (const [take, span] of spans) {
    if (span.start >= to) { span.start += delta; span.end += delta; }
    else if (span.end > from) spans.delete(take);
  }
}
/** A polish replaces only its own untouched span; user edits invalidate all stored offsets. */
export function applyDictation(value: string, selection: { start: number; end: number }, spans: DictationSpans, result: VoiceText, maxLength = Infinity): { value: string; caret: number } | 'too_long' | null {
  if (spans.box !== undefined && spans.box !== value) spans.clear();
  spans.box = value;
  if (result.phase === 'raw' && (spans.latestTake === undefined || result.take > spans.latestTake)) {
    spans.latestTake = result.take;
    const next = insertAtCaret(value, selection.start, selection.end, result.text);
    if (next.value === value) return null;
    if (next.value.length > maxLength) return 'too_long';
    shiftSpans(spans, Math.min(selection.start, selection.end), Math.max(selection.start, selection.end), next.value.length - value.length);
    spans.set(result.take, { start: next.start, end: next.end, text: next.value.slice(next.start, next.end) });
    for (const take of spans.keys()) if (spans.size > 8) spans.delete(take);
    spans.box = next.value;
    return { value: next.value, caret: next.end };
  }
  const span = spans.get(result.take);
  if (result.phase === 'polished') spans.delete(result.take);
  if (!span || selection.start !== selection.end || result.text === span.text) return null;
  const next = replaceIfUnchanged(value, span, span.text, result.text);
  if (!next || next.value.length > maxLength) return null;
  shiftSpans(spans, span.start, span.end, next.value.length - value.length);
  const caret = selection.start >= span.end ? selection.start + next.end - span.end : selection.start > span.start ? next.end : selection.start;
  if (result.phase === 'raw') spans.set(result.take, { start: span.start, end: next.end, text: result.text });
  spans.box = next.value;
  return { value: next.value, caret };
}
